import React, {useEffect, useState} from 'react';
import {Dropdown} from 'primereact/dropdown';
import {InputNumber} from "primereact/inputnumber";
import {InputText} from "primereact/inputtext";
import {useAppDispatch, useAppSelector} from "../../../redux/hooks";
import {getQueueDirectoriesQueueAudioType, getAllQueues} from "../../../redux/api/apiQueue";
import {InputTextarea} from "primereact/inputtextarea";
import {classNames} from "primereact/utils";
import {PROD} from "../../../redux/config";
import {isAudio, languages, voices} from "../../../dictionaries";
import {Button} from "primereact/button";
import {setCampaignACDField, setCompanyField, setEditType} from "../../../redux/actions/actions";
import {checkPermissions} from "../../../redux/permissions/permissionsUtils";
import {getAudioById, isValidAudioGroupId} from "../../../redux/api/apiAudio";
import AudioDialog from "../../Settings/AudioList/AudioDialog";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {MultiSelect} from "primereact/multiselect";

const ACDSettingsTab = () => {
    const dispatch = useAppDispatch()
    const companiesData = useAppSelector(state => state.CompaniesListReducer.companiesData);
    const queueData = useAppSelector(state => state.QueueListReducer.queueData);
    const campaignDirectoryCampaignType = useAppSelector(state => state.CompaniesListReducer.campaignDirectoryCampaignType);
    // @ts-ignore
    const queuesDirectoryQueueAudioType = useAppSelector(state => state.QueueListReducer.queuesDirectoryQueueAudioType);
    // @ts-ignore
    const allQueues = useAppSelector(state => state.QueueListReducer.allQueues);
    const archiveTableToggle = useAppSelector(state => state.CompaniesListReducer.archiveTableToggle);
    const editType = useAppSelector(state => state.UsersReducer.editType);
    const errorFields = useAppSelector(state => state.CompaniesListReducer.errorFields);
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);
    const rolesVision = useAppSelector(state => state.RolesReducer.rolesVision) as unknown as Record<string, string[]>;
    const dialerPermissions = checkPermissions(rolesVision, 'Audio');
    const detailsLoading = useAppSelector(state => state.AudioListReducer.detailsLoading);
    const campaignDetailsLoading = useAppSelector(state => state.CompaniesListReducer.detailsLoading);
    const audioDialogVisible = useAppSelector(state => state.AudioListReducer.audioDialogVisible);
    const [audioId, setAudioId] = useState<any>(null)
    const [field, setField] = useState<any>(null);
    const audioIsBlock = companiesData?.permission?.audioIsBlock;
    const statusDone = companiesData?.campaign?.statusId === 6;
    const disabledInputClass = classNames('w-full', {'opacity-100': archiveTableToggle && !statusDone && editType});

    const {
        visible: hasAudioVisiblePermission,
        update: hasAudioUpdatePermission,
    } = dialerPermissions;

    const sameAudioId = (a: any, b: any) =>
        a != null && b != null && String(a) === String(b);

    const findAccessibleAudio = (audioId: any) =>
        queuesDirectoryQueueAudioType?.find((audio: any) => sameAudioId(audio.audioId, audioId));

    const canOpenAudioDetails = (audioId: any) => {
        if (campaignDetailsLoading || audioIsBlock || !hasAudioVisiblePermission || audioId == null) {
            return false;
        }
        const audio = findAccessibleAudio(audioId);
        return isValidAudioGroupId(audio?.groupId);
    };

    const audioDetailsIcon = archiveTableToggle && hasAudioUpdatePermission ? 'pi pi-pencil' : 'pi pi-eye';


    const initializationOptions = [
        {name: 'Отримувач', id: 'Отримувач'},
    ];

    const filteredList = allQueues?.filter((item: { id: number; }) => item?.id != null && item.id !== queueData?.queue?.id);

    const filteredQueueList = filteredList?.map((item: { id: any; name: any; }) => (
        {
            ...item,
            idName: `ID: ${item.id} "${item.name}"`,
            stringId: `${item.id}`
        }));

    useEffect(() => {
        if (campaignDetailsLoading) {
            return;
        }
        if (audioIsBlock) {
            return;
        }
        dispatch(getQueueDirectoriesQueueAudioType(jwtToken?.jwtToken));
    }, [audioIsBlock, campaignDetailsLoading, jwtToken?.jwtToken]);

    useEffect(() => {
        let dictionaryId = 202;
        if (companiesData?.campaignACD?.schedulerStrategy == dictionaryId || companiesData?.campaignACD?.mwtStrategy == dictionaryId || companiesData?.campaignACD?.mwtNoAgentStrategy) {
            dispatch(getAllQueues(jwtToken?.jwtToken));
        }
    }, [companiesData?.campaignACD?.schedulerStrategy, companiesData?.campaignACD?.mwtStrategy, companiesData?.campaignACD?.mwtNoAgentStrategy]);

    const onAudioRowReorder = (e: { dragIndex: number; dropIndex: number }) => {
        let updatedAudios = [...(companiesData?.campaignACD?.ivmAudios || [])];
        const draggedRow = updatedAudios[e.dragIndex];

        updatedAudios.splice(e.dragIndex, 1);
        updatedAudios.splice(e.dropIndex, 0, draggedRow);

        updatedAudios = updatedAudios.map((item, index) => ({
            ...item,
            order: index + 1
        }));

        dispatch(setCompanyField({ section: 'campaignACD', fieldName: 'ivmAudios', value: updatedAudios }));
    };

    const audioItemsTable = () => {
        const value = audioIsBlock
            ? [...(companiesData?.campaignACD?.ivmAudios || [])]
                .sort((a: { order: number; }, b: { order: number; }) => a.order - b.order)
            : companiesData?.campaignACD?.ivmAudios
                ?.map((audioObj: any) => {
                    const found = queuesDirectoryQueueAudioType?.find((a: any) => sameAudioId(a.audioId, audioObj.audioId));
                    return found ? { ...found, order: audioObj.order } : null;
                })
                ?.filter(Boolean)
                ?.sort((a: { order: number; }, b: { order: number; }) => a.order - b.order);


        const recalculateAudioOrders = (audioList: { audioId: number }[]) => {
            return audioList.map((audio, index) => ({
                ...audio,
                order: index + 1
            }));
        };
        const handleRemoveAudio = (audioIdToRemove: number) => {
            const updated = companiesData?.campaignACD?.ivmAudios?.filter((item: any) => item.audioId !== audioIdToRemove) || [];
            const reordered = recalculateAudioOrders(updated);

            dispatch(setCompanyField({
                section: 'campaignACD',
                fieldName: 'ivmAudios',
                value: reordered
            }));
        };

        return (
            <DataTable
                value={value}
                reorderableRows={!audioIsBlock}
                onRowReorder={audioIsBlock ? undefined : onAudioRowReorder}
                className="w-full"
            >
                {!audioIsBlock && <Column rowReorder style={{ width: '3rem' }} />}
                <Column field="order" style={{ width: '30px' }} />
                <Column field="audioName" style={{ width: '90%' }}/>
                {!audioIsBlock && hasAudioVisiblePermission && <Column
                    className="px-0"
                    body={(rowData) => {
                        const accessibleAudio = findAccessibleAudio(rowData.audioId);
                        return canOpenAudioDetails(rowData.audioId) ? (
                        <Button
                            icon={audioDetailsIcon}
                            className="p-button-secondary pr-0"
                            loading={rowData?.audioId === audioId && detailsLoading}
                            onClick={() => {
                                if (!accessibleAudio?.groupId) {
                                    return;
                                }
                                dispatch(getAudioById(accessibleAudio.groupId, jwtToken?.jwtToken, true));
                                setAudioId(accessibleAudio.groupId);
                                dispatch(setEditType(!!hasAudioUpdatePermission));
                            }}
                            disabled={!archiveTableToggle || detailsLoading}
                        />
                        ) : null
                    }}
                />}
                {!audioIsBlock && <Column
                    className="px-0"
                    body={(rowData) => (
                        <Button
                            icon="pi pi-times"
                            className="p-button-secondary px-0 mx-1"
                            onClick={() => {
                                handleRemoveAudio(rowData.audioId);
                            }}
                            disabled={!archiveTableToggle || !editType}
                        />
                    )}
                    style={{ width: '3rem' }}
                />}
            </DataTable>
        );
    };

    return (
        <div className="acd-settings grid">
            <div className="p-fluid grid col-12 md:col-12">
                <div className="p-fluid field col-12 md:col-5 mb-0">
                    <div className={classNames('mb-4', {'p-error': errorFields?.ivmAudios})}>Аудіо очікування*</div>
                    {audioIsBlock ? (
                        <InputText
                            className={classNames(disabledInputClass, 'mr-2', {'p-invalid': errorFields?.ivmAudios})}
                            disabled
                            value={(companiesData?.campaignACD?.ivmAudios || [])
                                .map((a: any) => a.audioName)
                                .filter(Boolean)
                                .join(', ')}
                        />
                    ) : (
                        <MultiSelect
                            value={companiesData?.campaignACD?.ivmAudios?.map((a: any) => a.audioId) || [] || null}
                            onChange={(e) => {
                                const updated = e.value?.map((audioId: number, index: number) => {
                                    const audio = queuesDirectoryQueueAudioType?.find(
                                        (a: any) => a.audioId === audioId
                                    );
                                    return {
                                        audioId,
                                        order: index + 1,
                                        audioName: audio?.audioName || '',
                                        groupName: audio?.groupName || '',
                                        groupId: audio?.groupId,
                                    };
                                });
                                dispatch(setCompanyField({section: 'campaignACD', fieldName: 'ivmAudios', value: updated}));
                            }}
                            className={classNames('w-full mr-2', {'p-invalid': errorFields?.ivmAudios})}
                            options={queuesDirectoryQueueAudioType}
                            optionLabel="audioName"
                            disabled={!archiveTableToggle || !editType}
                            optionValue="audioId"
                            maxSelectedLabels={0}
                            selectedItemsLabel={companiesData?.campaignACD?.ivmAudios?.length ? `Обрано: ${companiesData?.campaignACD?.ivmAudios?.length}` : ""}
                            placeholder="Виберіть аудіо:"
                        />
                    )}
                    <div className="col-12 md:col-12 pt-2 px-0 pb-0">
                        {
                            !!companiesData?.campaignACD?.ivmAudios?.length &&
                            <div className="users-list">
                                {audioItemsTable()}
                            </div>
                        }
                    </div>
                    {errorFields?.ivmAudios && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><div
                        className="title p-error text-sm">{errorFields?.ivmAudios?.ivmAudios}</div></div>}
                </div>
                <div className="flex-column field col-12 md:col-3 mb-0">
                    <div>
                        <div className={classNames('mb-4', {'p-error': errorFields?.mohAudioTime})}>Тривалість відтворення (мін. 10 сек)*
                        </div>
                    </div>
                    <InputNumber
                        min={0}
                        id="maxWaitTime"
                        value={companiesData?.campaignACD?.mohAudioTime || null}
                        useGrouping={false}
                        className={classNames('', {'p-invalid': errorFields?.mohAudioTime})}
                        disabled={!archiveTableToggle || !editType}
                        onChange={(e) => {
                            dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mohAudioTime', value: e.value}));
                        }}
                        placeholder="0"
                    />
                    {errorFields?.mohAudioTime && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span
                        className="title p-error text-sm">{errorFields?.mohAudioTime?.mohAudioTime}</span></div>}
                </div>
                <div className="flex-column field col-12 md:col-3 mb-0">
                    <div>
                        <div className={classNames('mb-4', {'p-error': errorFields?.mohAudioTime})}>Тривалість розмови (мін. 10 сек)*
                        </div>
                    </div>
                    <InputNumber
                        min={0}
                        id="maxWaitTime"
                        // value={companiesData?.campaignACD?.mohAudioTime || null}
                        useGrouping={false}
                        className={classNames('', {'p-invalid': errorFields?.mohAudioTime})}
                        disabled={!archiveTableToggle || !editType}
                        // onChange={(e) => {
                        //     dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mohAudioTime', value: e.value}));
                        // }}
                        placeholder="0"
                    />
                </div>
                <div className="p-fluid field col-12 md:col-6 mb-0"></div>
            </div>

            {/*with agent*/}

            {/*{!PROD &&<div className="p-divider p-component p-divider-horizontal p-divider-solid p-divider-left"></div>}*/}
            {/*{!PROD && <div className="p-fluid grid col-12 md:col-12">*/}
            {/*    <div className="field col-12 md:col-3 mb-0">*/}
            {/*        <label className={classNames('', {'p-error': errorFields?.mwt})} htmlFor="initialization">Max wait time (мін 10 сек)*</label>*/}
            {/*        <InputNumber*/}
            {/*            min={0} id="maxWaitTime"*/}
            {/*            value={companiesData?.campaignACD?.mwt || null}*/}
            {/*            placeholder="0"*/}
            {/*            className={classNames('', {'p-invalid': errorFields?.mwt})}*/}
            {/*            useGrouping={false}*/}
            {/*            disabled={!archiveTableToggle || !editType}*/}
            {/*            onChange={(e) => {*/}
            {/*                if (!e.value) {*/}
            {/*                    dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwt', value: null}));*/}
            {/*                } else {*/}
            {/*                    dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwt', value: e.value}));*/}
            {/*                }*/}
            {/*            }}*/}

            {/*        />*/}
            {/*        {errorFields?.mwt && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span*/}
            {/*            className="title p-error text-sm">{errorFields?.mwt?.mwt}</span></div>}*/}
            {/*    </div>*/}
            {/*    <div className="field col-12 md:col-9 mb-0"></div>*/}
            {/*    <div className="field col-12 md:col-3 mb-0">*/}
            {/*        <label className={classNames('', {'p-error': errorFields?.mwtStrategy})} htmlFor="initialization">Action*</label>*/}
            {/*        <Dropdown*/}
            {/*            id="initialization"*/}
            {/*            value={companiesData?.campaignACD?.mwtStrategy || null}*/}
            {/*            options={campaignDirectoryCampaignType}*/}
            {/*            className={classNames('', {'p-invalid': errorFields?.mwtStrategy})}*/}
            {/*            optionLabel="name"*/}
            {/*            optionValue="dictionaryId"*/}
            {/*            disabled={!archiveTableToggle || !editType}*/}
            {/*            onChange={(e) => {*/}
            {/*                dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtStrategy', value: e.value}));*/}
            {/*                dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtStrategyAction', value: null}));*/}
            {/*            }}*/}
            {/*            placeholder="Виберіть дію"*/}
            {/*        />*/}
            {/*        {errorFields?.mwtStrategy && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span*/}
            {/*            className="title p-error text-sm">{errorFields?.mwtStrategy?.mwtStrategy}</span></div>}*/}
            {/*    </div>*/}
            {/*    <div className="field col-12 md:col-3 mb-0">*/}
            {/*        <label className={classNames('', {'p-error': errorFields?.mwtStrategyAction})} htmlFor="initialization">Отримувач</label>*/}
            {/*        {companiesData?.campaignACD?.mwtStrategy === 202 ? <Dropdown*/}
            {/*                id="initialization"*/}
            {/*                value={companiesData?.campaignACD?.mwtStrategyAction || null}*/}
            {/*                className={classNames('', {'p-invalid': errorFields?.mwtStrategyAction})}*/}
            {/*                options={filteredQueueList}*/}
            {/*                optionValue={'stringId'}*/}
            {/*                filter*/}
            {/*                panelClassName={'max-w-5rem'}*/}
            {/*                optionLabel={'idName'}*/}
            {/*                onChange={(e) => {*/}
            {/*                    dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtStrategyAction', value: e.value}));*/}
            {/*                }}*/}
            {/*                disabled={!archiveTableToggle || !editType}*/}
            {/*                placeholder="Виберіть дію"/> :*/}
            {/*            <Dropdown*/}
            {/*                id="initialization"*/}
            {/*                value={companiesData?.campaignACD?.mwtStrategyAction || null}*/}
            {/*                className={classNames('', {'p-invalid': errorFields?.mwtStrategyAction})}*/}
            {/*                options={initializationOptions}*/}
            {/*                onChange={(e) => {*/}
            {/*                    dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtStrategyAction', value: e.value}));*/}
            {/*                }}*/}
            {/*                optionValue={'id'}*/}
            {/*                panelClassName={'max-w-3rem'}*/}
            {/*                optionLabel={'name'}*/}
            {/*                disabled={!archiveTableToggle || !editType || companiesData?.campaignACD?.mwtStrategy === 201}*/}
            {/*                placeholder="Виберіть дію"/>}*/}
            {/*        {errorFields?.mwtStrategyAction && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span*/}
            {/*            className="title p-error text-sm">{errorFields?.mwtStrategyAction?.mwtStrategyAction}</span></div>}*/}
            {/*    </div>*/}
            {/*    <div className="field col-12 md:col-6 mb-0"></div>*/}
            {/*    <div className="field flex align-items-center col-12 md:col-3 mb-0">*/}
            {/*        <Dropdown*/}
            {/*            value={companiesData?.campaignACD?.isMWTAudio || false}*/}
            {/*            onChange={(e) => {*/}
            {/*                dispatch(setCompanyField({section: 'campaignACD', fieldName: 'isMWTAudio', value: e.value}));*/}
            {/*                if (!e.value) {*/}
            {/*                    dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtAudioId', value: null}));*/}
            {/*                    dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtAudioTime', value: null}));*/}
            {/*                }*/}
            {/*            }}*/}
            {/*            options={isAudio}*/}
            {/*            disabled={!archiveTableToggle || !editType}*/}
            {/*            optionLabel="name"*/}
            {/*            optionValue="state"*/}
            {/*            placeholder="Виберіть тип"*/}
            {/*            className="w-full"*/}
            {/*        />*/}
            {/*    </div>*/}
            {/*</div>}*/}
            {/*{companiesData?.campaignACD?.isMWTAudio && !PROD &&*/}
            {/*    <div className="p-fluid grid col-12 md:col-12">*/}
            {/*        <div className="field col-12 md:col-3 mb-0">*/}
            {/*            <label htmlFor="audioSource">Аудіо</label>*/}
            {/*            <Dropdown*/}
            {/*                id="audioSource"*/}
            {/*                value={companiesData?.campaignACD?.mwtAudioId || null}*/}
            {/*                options={queuesDirectoryQueueAudioType}*/}
            {/*                optionValue="audioId"*/}
            {/*                optionLabel="audioName"*/}
            {/*                disabled={!archiveTableToggle || !editType}*/}
            {/*                onChange={(e) => {*/}
            {/*                    dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtAudioId', value: e.value}));*/}
            {/*                }}*/}
            {/*                placeholder="Виберіть аудіо"/>*/}
            {/*            <div className="col-12 md:col-12 pt-2 px-0 pb-0">*/}
            {/*                {*/}
            {/*                    companiesData?.campaignACD?.mwtAudioId &&*/}
            {/*                    <div className="users-list">*/}
            {/*                        {audioItems(companiesData?.campaignACD?.mwtAudioId, 'mwtAudioId')}*/}
            {/*                    </div>*/}
            {/*                }*/}
            {/*            </div>*/}
            {/*        </div>*/}
            {/*        <div className="field col-12 md:col-3 mb-0">*/}
            {/*            <div className={classNames('mb-2', {'p-error': errorFields?.mwtAudioTime})}>Тривалість*/}
            {/*                відтворення (мін. 10 сек):*/}
            {/*            </div>*/}
            {/*            <InputNumber*/}
            {/*                min={0}*/}
            {/*                id="maxWaitTime"*/}
            {/*                value={companiesData?.campaignACD?.mwtAudioTime || null}*/}
            {/*                useGrouping={false}*/}
            {/*                className={classNames('', {'p-invalid': errorFields?.mwtAudioTime})}*/}
            {/*                placeholder="0"*/}
            {/*                disabled={!archiveTableToggle || !editType}*/}
            {/*                onChange={(e) => {*/}
            {/*                    dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtAudioTime', value: e.value}));*/}
            {/*                }}*/}
            {/*            />*/}
            {/*            {errorFields?.mwtAudioTime && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span*/}
            {/*                className="title p-error text-sm">{errorFields?.mwtAudioTime?.mwtAudioTime}</span></div>}*/}
            {/*        </div>*/}
            {/*    </div>}*/}
            {/*{!companiesData?.campaignACD?.isMWTAudio && <div className="p-fluid grid col-12 md:col-12">*/}
            {/*    <div className="field col-12 md:col-3 mb-0">*/}
            {/*        <label htmlFor="audioSource">Мова *</label>*/}
            {/*        <Dropdown*/}
            {/*            id="audioSource"*/}
            {/*            value={companiesData?.campaignACD?.mwttts?.language || null}*/}
            {/*            options={languages}*/}
            {/*            optionLabel="name"*/}
            {/*            optionValue="id"*/}
            {/*            disabled={!archiveTableToggle || !editType}*/}
            {/*            onChange={(e) => {*/}
            {/*                dispatch(setCampaignACDField({section: 'campaignACD', fieldName: 'mwttts', acdFieldName: 'language', value: e.value}));*/}
            {/*            }}*/}
            {/*            placeholder="Виберіть мову"/>*/}
            {/*    </div>*/}
            {/*    <div className="field col-12 md:col-3 mb-0">*/}
            {/*        <label htmlFor="audioSource">Голос</label>*/}
            {/*        <Dropdown*/}
            {/*            id="audioSource"*/}
            {/*            value={companiesData?.campaignACD?.mwttts?.sex || null || null}*/}
            {/*            options={voices}*/}
            {/*            optionLabel="name"*/}
            {/*            optionValue="id"*/}
            {/*            disabled={!archiveTableToggle || !editType}*/}
            {/*            onChange={(e) => {*/}
            {/*                dispatch(setCampaignACDField({section: 'campaignACD', fieldName: 'mwttts', acdFieldName: 'sex', value: e.value}));*/}
            {/*            }}*/}
            {/*            placeholder="Виберіть голос"/>*/}
            {/*    </div>*/}
            {/*    <div className="field col-12 md:col-6 mb-0"></div>*/}
            {/*    <div className="field col-12 md:col-6 mb-0">*/}
            {/*        <label htmlFor="audioSource">Текст повідомлення:</label>*/}
            {/*        <InputTextarea*/}
            {/*            autoResize*/}
            {/*            value={companiesData?.campaignACD?.mwttts?.text || ''}*/}
            {/*            onChange={(e) =>*/}
            {/*                dispatch(setCampaignACDField({section: 'campaignACD', fieldName: 'mwttts', acdFieldName: 'text', value: e.target.value}))*/}
            {/*            }*/}
            {/*            rows={5}*/}
            {/*            cols={30}*/}
            {/*            maxLength={150}*/}
            {/*            placeholder="Введіть текст повідомлення"*/}
            {/*        />*/}
            {/*    </div>*/}
            {/*</div>}*/}

            {/*/!*no agent*!/*/}

            {/*{!PROD && <div className="p-divider p-component p-divider-horizontal p-divider-solid p-divider-left"></div>}*/}
            {/*{!PROD && <div className="p-fluid grid col-12 md:col-12">*/}
            {/*    <div className="field col-12 md:col-3 mb-0">*/}
            {/*        <label className={classNames('', {'p-error': errorFields?.mwtNoAgent})} htmlFor="initialization">Max wait time with no agent (мін 1 сек)*</label>*/}
            {/*        <InputNumber min={1}*/}
            {/*                     id="mwtNoAgent"*/}
            {/*                     value={companiesData?.campaignACD?.mwtNoAgent || null}*/}
            {/*                     useGrouping={false}*/}
            {/*                     className={classNames('', {'p-invalid': errorFields?.mwtNoAgent})}*/}
            {/*                     placeholder="0"*/}
            {/*                     disabled={!archiveTableToggle || !editType}*/}
            {/*                     onChange={(e) => {*/}
            {/*                         if (!e.value) {*/}
            {/*                             dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtNoAgent', value: null}));*/}
            {/*                         } else {*/}
            {/*                             dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtNoAgent', value: e.value}));*/}
            {/*                         }*/}
            {/*                     }}*/}
            {/*        />*/}
            {/*        {errorFields?.mwtNoAgent && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span*/}
            {/*            className="title p-error text-sm">{errorFields?.mwtNoAgent?.mwtNoAgent}</span></div>}*/}
            {/*    </div>*/}
            {/*    <div className="field col-12 md:col-9 mb-0"></div>*/}
            {/*    <div className="field col-12 md:col-3 mb-0">*/}
            {/*        <label className={classNames('', {'p-error': errorFields?.mwtNoAgentStrategy})} htmlFor="initialization">Action*</label>*/}

            {/*        <Dropdown*/}
            {/*            id="initialization"*/}
            {/*            value={companiesData?.campaignACD?.mwtNoAgentStrategy || null}*/}
            {/*            options={campaignDirectoryCampaignType}*/}
            {/*            optionLabel="name"*/}
            {/*            className={classNames('', {'p-invalid': errorFields?.mwtNoAgentStrategy})}*/}
            {/*            disabled={!archiveTableToggle || !editType}*/}
            {/*            optionValue="dictionaryId"*/}
            {/*            onChange={(e) => {*/}
            {/*                dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtNoAgentStrategy', value: e.value}));*/}
            {/*                dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtNoAgentStrategyAction', value: null}));*/}
            {/*            }}*/}
            {/*            placeholder="Виберіть дію"/>*/}
            {/*        {errorFields?.mwtNoAgentStrategy && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span*/}
            {/*            className="title p-error text-sm">{errorFields?.mwtNoAgentStrategy?.mwtNoAgentStrategy}</span></div>}*/}
            {/*    </div>*/}
            {/*    <div className="field col-12 md:col-3 mb-0">*/}
            {/*        <label className={classNames('', {'p-error': errorFields?.mwtNoAgentStrategyAction})} htmlFor="initialization">Отримувач</label>*/}
            {/*        {companiesData?.campaignACD?.mwtNoAgentStrategy === 202  ? <Dropdown*/}
            {/*                id="initialization"*/}
            {/*                value={companiesData?.campaignACD?.mwtNoAgentStrategyAction || null}*/}
            {/*                options={filteredQueueList}*/}
            {/*                className={classNames('', {'p-invalid': errorFields?.mwtNoAgentStrategyAction})}*/}
            {/*                optionValue={'stringId'}*/}
            {/*                panelClassName={'max-w-5rem'}*/}
            {/*                optionLabel={'idName'}*/}
            {/*                filter*/}
            {/*                onChange={(e) => {*/}
            {/*                    dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtNoAgentStrategyAction', value: e.value}));*/}
            {/*                }}*/}
            {/*                disabled={!archiveTableToggle || !editType}*/}
            {/*                placeholder="Виберіть дію"/> :*/}
            {/*            <Dropdown*/}
            {/*                id="initialization"*/}
            {/*                value={companiesData?.campaignACD?.mwtNoAgentStrategyAction || null}*/}
            {/*                options={initializationOptions}*/}
            {/*                className={classNames('', {'p-invalid': errorFields?.mwtNoAgentStrategyAction})}*/}
            {/*                onChange={(e) => {*/}
            {/*                    dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtNoAgentStrategyAction', value: e.value}));*/}
            {/*                }}*/}
            {/*                optionValue={'id'}*/}
            {/*                panelClassName={'max-w-3rem'}*/}
            {/*                optionLabel={'name'}*/}
            {/*                disabled={!archiveTableToggle || !editType || companiesData?.campaignACD?.mwtNoAgentStrategy === 201}*/}
            {/*                placeholder="Виберіть дію"/>}*/}
            {/*        {errorFields?.mwtNoAgentStrategyAction && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span*/}
            {/*            className="title p-error text-sm">{errorFields?.mwtNoAgentStrategyAction?.mwtNoAgentStrategyAction}</span></div>}*/}
            {/*    </div>*/}
            {/*</div>}*/}
            {/*{!PROD &&<div className="field flex align-items-center col-12 md:col-3 mb-0">*/}
            {/*    <Dropdown*/}
            {/*        value={companiesData?.campaignACD?.isMWTNoAgentAudio || false}*/}
            {/*        onChange={(e) => {*/}
            {/*            dispatch(setCompanyField({section: 'campaignACD', fieldName: 'isMWTNoAgentAudio', value: e.value}));*/}
            {/*            if (!e.value) {*/}
            {/*                dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtNoAgentAudioId', value: null}));*/}
            {/*                dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtNoAgentAudioTime', value: null}));*/}
            {/*            }*/}
            {/*        }}*/}
            {/*        options={isAudio}*/}
            {/*        disabled={!archiveTableToggle || !editType}*/}
            {/*        optionLabel="name"*/}
            {/*        optionValue="state"*/}
            {/*        placeholder="Виберіть тип"*/}
            {/*        className="w-full mr-2"*/}
            {/*    />*/}
            {/*</div>}*/}
            {/*{companiesData?.campaignACD?.isMWTNoAgentAudio && !PROD && <div className="p-fluid grid col-12 md:col-12">*/}
            {/*    <div className="field col-12 md:col-3 mb-0">*/}
            {/*        <label htmlFor="audioSource">Аудіо</label>*/}
            {/*        <Dropdown id="audioSource" value={companiesData?.campaignACD?.mwtNoAgentAudioId || null}*/}
            {/*                  options={queuesDirectoryQueueAudioType}*/}
            {/*                  onChange={(e) => {*/}
            {/*                      dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtNoAgentAudioId', value: e.value}));*/}
            {/*                  }}*/}
            {/*                  disabled={!archiveTableToggle || !editType}*/}
            {/*                  optionLabel="audioName"*/}
            {/*                  optionValue="audioId"*/}
            {/*                  placeholder="Виберіть аудіо"/>*/}
            {/*        <div className="col-12 md:col-12 pt-2 px-0 pb-0">*/}
            {/*            {*/}
            {/*                companiesData?.campaignACD?.mwtNoAgentAudioId &&*/}
            {/*                <div className="users-list">*/}
            {/*                    {audioItems(companiesData?.campaignACD?.mwtNoAgentAudioId, 'mwtNoAgentAudioId')}*/}
            {/*                </div>*/}
            {/*            }*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*    <div className="field col-12 md:col-3 mb-0">*/}
            {/*        <div className={classNames('mb-2', {'p-error': errorFields?.mwtNoAgentAudioTime})}>Тривалість*/}
            {/*            відтворення (мін. 10 сек):*/}
            {/*        </div>*/}
            {/*        <InputNumber*/}
            {/*            min={0}*/}
            {/*            id="maxWaitTime"*/}
            {/*            placeholder="0"*/}
            {/*            className={classNames('', {'p-invalid': errorFields?.mwtNoAgentAudioTime})}*/}
            {/*            value={companiesData?.campaignACD?.mwtNoAgentAudioTime || null}*/}
            {/*            useGrouping={false}*/}
            {/*            disabled={!archiveTableToggle || !editType}*/}
            {/*            onChange={(e) => {*/}
            {/*                dispatch(setCompanyField({section: 'campaignACD', fieldName: 'mwtNoAgentAudioTime', value: e.value}));*/}
            {/*            }}*/}
            {/*        />*/}
            {/*        {errorFields?.mwtNoAgentAudioTime && <div className="col-12 md:col-12 pt-2 px-0 pb-0"><span*/}
            {/*            className="title p-error text-sm">{errorFields?.mwtNoAgentAudioTime?.mwtNoAgentAudioTime}</span>*/}
            {/*        </div>}*/}
            {/*    </div>*/}
            {/*</div>}*/}
            {/*{!companiesData?.campaignACD?.isMWTNoAgentAudio && <div className="p-fluid grid col-12 md:col-12">*/}
            {/*    <div className="field col-12 md:col-3 mb-0">*/}
            {/*        <label htmlFor="audioSource">Мова *</label>*/}
            {/*        <Dropdown*/}
            {/*            id="audioSource"*/}
            {/*            value={companiesData?.campaignACD?.mwtNoAgentTTS?.language || null}*/}
            {/*            options={languages}*/}
            {/*            optionLabel="name"*/}
            {/*            optionValue="id"*/}
            {/*            disabled={!archiveTableToggle || !editType}*/}
            {/*            onChange={(e) => {*/}
            {/*                dispatch(setCampaignACDField({section: 'campaignACD', fieldName: 'mwtNoAgentTTS', acdFieldName: 'language', value: e.value}));*/}
            {/*            }}*/}
            {/*            placeholder="Виберіть мову"/>*/}
            {/*    </div>*/}
            {/*    <div className="field col-12 md:col-3 mb-0">*/}
            {/*        <label htmlFor="audioSource">Голос</label>*/}
            {/*        <Dropdown*/}
            {/*            id="audioSource"*/}
            {/*            value={companiesData?.campaignACD?.mwtNoAgentTTS?.sex || null}*/}
            {/*            options={voices}*/}
            {/*            optionLabel="name"*/}
            {/*            optionValue="id"*/}
            {/*            disabled={!archiveTableToggle || !editType}*/}
            {/*            onChange={(e) => {*/}
            {/*                dispatch(setCampaignACDField({section: 'campaignACD', fieldName: 'mwtNoAgentTTS', acdFieldName: 'sex', value: e.value}));*/}
            {/*            }}*/}
            {/*            placeholder="Виберіть голос"/>*/}
            {/*    </div>*/}
            {/*    <div className="field col-12 md:col-6 mb-0"></div>*/}
            {/*    <div className="field col-12 md:col-6 mb-0">*/}
            {/*        <label htmlFor="audioSource">Текст повідомлення:</label>*/}
            {/*        <InputTextarea*/}
            {/*            autoResize*/}
            {/*            value={companiesData?.campaignACD?.mwtNoAgentTTS?.text || ''}*/}
            {/*            onChange={(e) =>*/}
            {/*                dispatch(setCampaignACDField({section: 'campaignACD', fieldName: 'mwtNoAgentTTS', acdFieldName: 'text', value: e.target.value}))*/}
            {/*            }*/}
            {/*            rows={5}*/}
            {/*            cols={30}*/}
            {/*            maxLength={150}*/}
            {/*            placeholder="Введіть текст повідомлення"*/}
            {/*        />*/}
            {/*    </div>*/}
            {/*</div>}*/}
            {audioDialogVisible && (
                <AudioDialog skipBranchesFetch={companiesData?.permission?.branchIsBlock}/>
            )}
        </div>
    );
};

export default ACDSettingsTab;
